import * as THREE from 'three';
import {
  CanvasListenersType,
  DataDrainFunnel,
  DrainLineObjectType,
  EditType,
  RoomType,
  SaveSectorStorageType,
} from '../../types';
import { COLORS_CONST } from '../../../helpers/styles';
import { getCenterMass } from '../../js';
import { Room } from '../Room';
import { CircleCrossMove } from './classes/CircleCrossMove';
import { LineLinkPoint } from './classes/LineLinkPoint';
import { PlaneMove } from './classes/PlaneMove';

interface ConstructorProps {
  points: Array<THREE.Vector3>;
  rooms: Array<Array<number>>;
  Edit: EditType;
  canvasListener?: CanvasListenersType;
  savedDrainFunnel?: SaveSectorStorageType['drainFunnelObjects'];
}
export class DrainLineObject extends THREE.Group implements DrainLineObjectType {
  static SizePlane = 0.5;
  private symbolsPoints: Array<string> = [];
  private readonly points: Array<THREE.Vector3>;
  private rooms: Array<Array<number>>;
  material = new THREE.MeshBasicMaterial({
    color: COLORS_CONST.red,
    side: THREE.DoubleSide,
    // wireframe: true,
  });
  Edit: EditType;
  planesIntersected: Array<RoomType> = [];
  planesMove: Array<{ start: PlaneMove; end: PlaneMove }> = [];

  constructor({ points, rooms, Edit, savedDrainFunnel }: ConstructorProps) {
    super();
    this.name = 'drain_line_object';
    this.points = points;
    this.rooms = rooms;
    this.Edit = Edit;

    this.getSymbolsControlsPoints();
    this.buildGroup(savedDrainFunnel);
  }

  buildGroup(savedDrainFunnel?: SaveSectorStorageType['drainFunnelObjects']) {
    this.rooms.forEach(room => {
      const controls = room.map(item => this.points[item]);
      const roomDottedName = room.join('');
      let currentSavedDrainFunnel;
      if (savedDrainFunnel && savedDrainFunnel.objects.length) {
        currentSavedDrainFunnel = savedDrainFunnel.objects.find(
          item => item.roomDottedName === roomDottedName
        );
      }

      const center = getCenterMass(controls).setY(0);
      const posStart = center.clone().add(new THREE.Vector3(-0.6, 0, 0));
      const posEnd = center.clone().add(new THREE.Vector3(0.6, 0, 0));
      if (currentSavedDrainFunnel) {
        posStart.x = currentSavedDrainFunnel.position.x;
        posStart.y = currentSavedDrainFunnel.position.y;
        posStart.z = currentSavedDrainFunnel.position.z;
        posEnd.x = currentSavedDrainFunnel?.end?.x || 0;
        posEnd.y = currentSavedDrainFunnel?.end?.y || 0;
        posEnd.z = currentSavedDrainFunnel?.end?.z || 0;
      }

      if (!savedDrainFunnel || currentSavedDrainFunnel) {
        const symbolStart = this.getFreeSymbol() + 'v';
        const symbolEnd = this.getFreeSymbol() + 'v';

        const circleStart = new CircleCrossMove({
          symbol: symbolStart,
        });
        circleStart.position.copy(posStart);
        const circleEnd = new CircleCrossMove({
          symbol: symbolEnd,
        });
        circleEnd.position.copy(posEnd);
        const line = new LineLinkPoint({
          start: posStart.clone(),
          end: posEnd.clone(),
          symbol: `${symbolStart}-${symbolEnd}`,
        });
        this.add(circleStart, circleEnd, line);

        const _room = new Room({
          contour: controls.map(item => item.clone()),
          dottedName: roomDottedName,
          drainFunnelLine: {
            start: posStart.clone(),
            end: posEnd.clone(),
          },
        });
        this.planesIntersected.push(_room);

        const planeMoveStart = new PlaneMove({
          position: posStart.clone(),
          moveObject: circleStart,
          lineSideUpdate: 'start',
          lineMove: line,
          planeIntersected: _room,
        });
        const planeMoveEnd = new PlaneMove({
          position: posEnd.clone(),
          moveObject: circleEnd,
          lineSideUpdate: 'end',
          lineMove: line,
          planeIntersected: _room,
        });
        this.Edit.dragObject?.addObjectMove(planeMoveStart);
        this.Edit.dragObject?.addObjectMove(planeMoveEnd);

        this.planesMove.push({
          start: planeMoveStart,
          end: planeMoveEnd,
        });
        this.add(planeMoveStart, planeMoveEnd);
      }
    });
  }
  updateHeightControlPoints() {
    this.children.forEach(item => {
      if (item instanceof PlaneMove) {
        item.position.y = 0;
        item.dragStart();
        item.drag();
        item.dragEnd();
      }
    });
  }
  changeHeightFunnel(height: number, uuid: string) {
    this.children.forEach(item => {
      if (item instanceof PlaneMove && item.lineMove.uuid === uuid) {
        item.position.y = height;
        item.dragStart();
        item.drag();
        item.dragEnd();
      }
    });
  }
  getParamsData(): Array<DataDrainFunnel> {
    const res: Array<DataDrainFunnel> = [];
    this.children.forEach(item => {
      if (item instanceof LineLinkPoint) {
        res.push({
          heightDrainFunnel: (item.geometry as THREE.Geometry).vertices[0].y,
          symbolFunnel: item.symbol,
          uuid: item.uuid,
        });
      }
    });
    return res;
  }
  removeGroup() {
    this.material.dispose();
    while (this.children.length > 0) {
      if (
        this.children[0] instanceof LineLinkPoint ||
        this.children[0] instanceof CircleCrossMove
      ) {
        this.children[0].removeMesh();
      } else if (this.children[0] instanceof PlaneMove) {
        this.Edit.dragObject?.removeObjectMove(this.children[0].uuid);
        // this.canvasListener?.removeObjectsIntersected(this.children[0].uuid);
        this.children[0].removeMesh();
      }
    }
    while (this.planesIntersected.length > 0) {
      this.planesIntersected[0].geometry.dispose();
      this.planesIntersected[0].material.dispose();
      this.planesIntersected.shift();
    }
    this.parent?.remove(this);
  }

  private getSymbolsControlsPoints = () => {
    for (let i = 65; i < 91; i++) {
      this.symbolsPoints.push(String.fromCharCode(i));
    }
  };
  private getFreeSymbol(): string {
    return this.symbolsPoints.shift() || '$';
  }
}
